/**
 * Manually derived from: https://shopify.dev/api/admin/rest/reference/orders/refund
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyReverseDeliveryKey,
  ShopifyReverseDeliveryLineItemKey,
  ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
  ShopifyReverseDeliveryLineItemToReverseFulfillmentOrderDispositionsKey,
  ShopifyReverseDeliveryLineItemToReverseFulfillmentOrderLineItemKey,
  ShopifyReverseDeliveryLineItemToShopKey,
  ShopifyReverseFulfillmentOrderDispositionKey,
  ShopifyReverseFulfillmentOrderDispositionToReverseDeliveryLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemKey,
  ShopifyReverseFulfillmentOrderLineItemToReverseDeliveryLineItemsKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyReverseDeliveryLineItemKey,
  "2024-07": {
    name: "Shopify Reverse Delivery Line Item",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Quantity",
        fieldType: FieldType.Number,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "quantity" } },
      },
      {
        name: "Dispositions",
        fieldType: FieldType.HasMany,
        key: ShopifyReverseDeliveryLineItemToReverseFulfillmentOrderDispositionsKey,
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderDispositionKey,
          inverseFieldKey: ShopifyReverseFulfillmentOrderDispositionToReverseDeliveryLineItemKey,
        },
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "dispositions",
            graphQLFieldKey: "dispositions",
            graphQLSelection: `
          id
          quantity
          type
          location {
            id
          }
          `,
            selectForHasMany: true,
            requiredScopes: ["read_locations"],
          },
        },
      },
      {
        name: "Reverse Fulfillment Order Line Item",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseDeliveryLineItemToReverseFulfillmentOrderLineItemKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "reverse_fulfillment_order_line_item_id",
            graphQLFieldKey: "reverseFulfillmentOrderLineItem",
            graphQLSelection: `id`,
          },
        },
        configuration: {
          relatedModelKey: ShopifyReverseFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyReverseFulfillmentOrderLineItemToReverseDeliveryLineItemsKey,
        },
      },
      {
        name: "Reverse Delivery",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "reverse_delivery_id",
            graphQLFieldKey: "reverseDeliveryId",
            skipGraphQLSelection: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyReverseDeliveryKey,
          inverseFieldKey: ShopifyReverseDeliveryLineItemToReverseDeliveryKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyReverseDeliveryLineItemToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_sublist",
      parent: "reverseFulfillmentOrder.reverseDeliveries",
      resource: "reverseDelivery.reverseDeliveryLineItems",
      additionalFields: ["reverseDeliveryId:id"],
    },
  },
};

export const ShopifyReverseDeliveryLineItem = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
